import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Button, Form, InputGroup, Pagination, Alert, Badge } from 'react-bootstrap';
import { FaPlus, FaSearch, FaEye, FaFileDownload, FaFilter } from 'react-icons/fa';
import api from '../../services/api';
import { toast } from 'react-toastify';
import Spinner from '../../components/layout/Spinner';

const InvoiceList = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialStatus = queryParams.get('status') || '';

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('invoiceDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [customerFilter, setCustomerFilter] = useState('');
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    fetchInvoices();
    fetchCustomers();
  }, [currentPage, sortField, sortOrder, statusFilter, customerFilter]);

  const fetchInvoices = async (search = searchTerm) => {
    try {
      setLoading(true);
            const res = await api.get('/invoices', {
        params: {
          page: currentPage,
          limit: 10,
          search,
          sortField,
          sortOrder,
          paymentStatus: statusFilter,
          customer: customerFilter
        }
      });
      
      setInvoices(res.data.invoices);
      setTotalPages(res.data.pagination.pages);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setError('Failed to load invoices');
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
            const res = await api.get('/customers', {
        params: {
          limit: 100, // Get a reasonable number of customers for the filter
          sortField: 'name',
          sortOrder: 'asc'
        }
      });
      setCustomers(res.data.customers);
    } catch (err) {
      console.error('Error fetching customers for filter:', err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchInvoices(searchTerm);
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleCustomerFilterChange = (e) => {
    setCustomerFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleDownload = async (id) => {
    try {
            const response = await api.get(`/invoices/${id}/pdf`, {
        responseType: 'blob'
      });
      
      // Create a blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${id}.pdf`);
      
      // Append to html page
      document.body.appendChild(link);
      
      // Force download
      link.click();
      
      // Clean up and remove the link
      link.parentNode.removeChild(link);
      
      toast.success('Invoice downloaded successfully');
    } catch (err) {
      console.error('Error downloading invoice:', err);
      toast.error('Failed to download invoice');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge bg="success">Paid</Badge>;
      case 'partial':
        return <Badge bg="warning">Partial</Badge>;
      case 'pending':
        return <Badge bg="danger">Pending</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxPagesToShow = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    // Previous button
    pages.push(
      <Pagination.Item 
        key="prev" 
        disabled={currentPage === 1}
        onClick={() => handlePageChange(currentPage - 1)}
      >
        &laquo;
      </Pagination.Item>
    );
    
    // First page
    if (startPage > 1) {
      pages.push(
        <Pagination.Item 
          key={1} 
          active={1 === currentPage}
          onClick={() => handlePageChange(1)}
        >
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        pages.push(<Pagination.Ellipsis key="ellipsis1" />);
      }
    }
    
    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Pagination.Item 
          key={i} 
          active={i === currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    
    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<Pagination.Ellipsis key="ellipsis2" />);
      }
      pages.push(
        <Pagination.Item 
          key={totalPages} 
          active={totalPages === currentPage}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }
    
    // Next button
    pages.push(
      <Pagination.Item 
        key="next" 
        disabled={currentPage === totalPages}
        onClick={() => handlePageChange(currentPage + 1)}
      >
        &raquo;
      </Pagination.Item>
    );
    
    return <Pagination>{pages}</Pagination>;
  };

  if (loading && invoices.length === 0) {
    return <Spinner />;
  }

  return (
    <Container>
      <Row className="mb-4 align-items-center">
        <Col>
          <h1>Invoices</h1>
        </Col>
        <Col xs="auto">
          <Button as={Link} to="/invoices/new" variant="primary">
            <FaPlus className="me-2" /> Create New Invoice
          </Button>
        </Col>
      </Row>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      <Card className="mb-4">
        <Card.Header>
          <FaFilter className="me-2" /> Filters
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <Form onSubmit={handleSearch}>
                <InputGroup className="mb-3">
                  <Form.Control
                    type="text"
                    placeholder="Search by invoice number"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Button type="submit" variant="primary">
                    <FaSearch /> Search
                  </Button>
                </InputGroup>
              </Form>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Filter by Status</Form.Label>
                <Form.Select 
                  value={statusFilter} 
                  onChange={handleStatusFilterChange}
                >
                  <option value="">All Statuses</option>
                  <option value="paid">Paid</option>
                  <option value="partial">Partially Paid</option>
                  <option value="pending">Pending</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label>Filter by Customer</Form.Label>
                <Form.Select 
                  value={customerFilter} 
                  onChange={handleCustomerFilterChange}
                >
                  <option value="">All Customers</option>
                  {customers.map(customer => (
                    <option key={customer._id} value={customer._id}>
                      {customer.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner />
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-4">
              <p className="mb-3">No invoices found.</p>
              <Button as={Link} to="/invoices/new" variant="primary">
                Create New Invoice
              </Button>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('invoiceNumber')} style={{ cursor: 'pointer' }}>
                        Invoice # {renderSortIcon('invoiceNumber')}
                      </th>
                      <th>Customer</th>
                      <th onClick={() => handleSort('invoiceDate')} style={{ cursor: 'pointer' }}>
                        Date {renderSortIcon('invoiceDate')}
                      </th>
                      <th onClick={() => handleSort('dueDate')} style={{ cursor: 'pointer' }}>
                        Due Date {renderSortIcon('dueDate')}
                      </th>
                      <th onClick={() => handleSort('totalAmount')} style={{ cursor: 'pointer' }}>
                        Amount {renderSortIcon('totalAmount')}
                      </th>
                      <th>Status</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice._id}>
                        <td>{invoice.invoiceNumber}</td>
                        <td>
                          <Link to={`/customers/${invoice.customer._id}`}>
                            {invoice.customer.name}
                          </Link>
                        </td>
                        <td>{new Date(invoice.invoiceDate).toLocaleDateString()}</td>
                        <td>
                          {invoice.dueDate ? 
                            new Date(invoice.dueDate).toLocaleDateString() : 
                            'N/A'}
                        </td>
                        <td>${invoice.total ? invoice.total.toFixed(2) : '0.00'}</td>
                        <td>{getStatusBadge(invoice.paymentStatus)}</td>
                        <td className="text-center">
                          <Button
                            as={Link}
                            to={`/invoices/${invoice._id}`}
                            variant="outline-primary"
                            size="sm"
                            className="me-1"
                            title="View Invoice"
                          >
                            <FaEye />
                          </Button>
                          <Button
                            variant="outline-info"
                            size="sm"
                            title="Download Invoice"
                            onClick={() => handleDownload(invoice._id)}
                          >
                            <FaFileDownload />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              
              <div className="d-flex justify-content-center mt-4">
                {renderPagination()}
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default InvoiceList;
